import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Palette, Eye, Send, Copy, Check, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { permissions } from '@/lib/mockAuth';
import { isTrialWedding } from '@/utils/trialUtils';
import { einviteService } from '@/services/einvite.service';

// Template designer intentionally disabled for now.
// Template components + selection UI are commented out to avoid layout issues.

export default function EInviteModule({ weddingId, wedding }) {
  const { userRole, activeOrg } = useAuth();
  const canManage = permissions.canEditWeddings(userRole);
  // A user is on trial if they have no credits AND the wedding is a trial wedding
  // If they have credits > 0, they are paid (even if wedding was originally a trial)
  const isTrial = (activeOrg?.credits === 0 || !activeOrg?.credits) && isTrialWedding(wedding);
  
  const [inviteType, setInviteType] = useState('video'); // 'video' | 'image'
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState('');
  const [uploadedVideoUrl, setUploadedVideoUrl] = useState('');
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [inviteData, setInviteData] = useState({
    brideName: '',
    groomName: '',
    brideParents: '',
    groomParents: '',
    venue: '',
    venueAddress: '',
    weddingDate: '',
    weddingTime: '',
    quote: '',
    message: '',
    rsvpLink: '',
    // Additional editable text fields for templates
    headerText: 'Together with their families',
    connectorText: 'WITH',
    requestText: 'Request the pleasure of your company',
    // Text positions (x in percentage, y in pixels)
    textPositions: {
      brideName: { x: 50, y: 80 },
      groomName: { x: 50, y: 160 },
      weddingDate: { x: 50, y: 60 },
      venue: { x: 50, y: 120 },
      quote: { x: 50, y: 40 },
    },
  });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [published, setPublished] = useState(false);
  const [publishedInvite, setPublishedInvite] = useState(null);
  const [allPublishedInvites, setAllPublishedInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [loadingInvites, setLoadingInvites] = useState(false);

  const isVideoInvite = inviteType === 'video';
  const isImageInvite = inviteType === 'image';

  useEffect(() => {
    return () => {
      if (videoPreviewUrl) {
        try { URL.revokeObjectURL(videoPreviewUrl); } catch (e) {}
      }
      if (imagePreviewUrl) {
        try { URL.revokeObjectURL(imagePreviewUrl); } catch (e) {}
      }
    };
  }, [videoPreviewUrl, imagePreviewUrl]);

  // Auto-fill from wedding data
  useEffect(() => {
    if (wedding) {
      let formattedDate = '';
      if (wedding.wedding_date) {
        try {
          const date = new Date(wedding.wedding_date);
          formattedDate = date.toLocaleDateString('en-IN', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          });
        } catch (e) {
          formattedDate = wedding.wedding_date;
        }
      }

      // Normalize venue/location (wedding.venue_location can be a Google place object)
      const venueLocation = wedding.venue_location;
      let normalizedVenueAddress = '';
      let normalizedVenueFromLocation = '';
      if (typeof venueLocation === 'string') {
        normalizedVenueAddress = venueLocation;
      } else if (venueLocation && typeof venueLocation === 'object') {
        normalizedVenueAddress = venueLocation.address || '';
        normalizedVenueFromLocation = venueLocation.name || '';
      }

      const normalizedVenue =
        typeof wedding.venue === 'string'
          ? wedding.venue
          : (wedding.venue && typeof wedding.venue === 'object' ? (wedding.venue.name || '') : '');

      setInviteData(prev => ({
        ...prev,
        brideName: wedding.bride_name || '',
        groomName: wedding.groom_name || '',
        venue: normalizedVenue || normalizedVenueFromLocation || '',
        venueAddress: normalizedVenueAddress,
        weddingDate: formattedDate,
      }));
    }
  }, [wedding]);

  const handlePreview = () => {
    if (isVideoInvite) {
      if (!videoFile && !uploadedVideoUrl) {
        toast.error('Please upload a video invite first');
        return;
      }
    }
    if (isImageInvite) {
      if (!imageFile && !uploadedImageUrl) {
        toast.error('Please upload an image invite first');
        return;
      }
    }
    setPreviewOpen(true);
  };

  const handlePublish = async () => {
    if (isTrial) {
      toast.error('Trial users cannot publish E-invites. Please upgrade to publish and share your invitations.');
      return;
    }

    if (isVideoInvite) {
      if (!videoFile && !uploadedVideoUrl) {
        toast.error('Please upload a video invite first');
        return;
      }
    }
    if (isImageInvite) {
      if (!imageFile && !uploadedImageUrl) {
        toast.error('Please upload an image invite first');
        return;
      }
    }

    setLoading(true);
    try {
      let finalInviteData = inviteData;
      if (isVideoInvite) {
        let videoUrl = uploadedVideoUrl;
        if (!videoUrl && videoFile) {
          setUploadingVideo(true);
          const up = await einviteService.uploadVideoInvite(weddingId, activeOrg?.id, videoFile);
          videoUrl = up?.video_url;
          setUploadedVideoUrl(videoUrl || '');
          setUploadingVideo(false);
        }
        if (!videoUrl) {
          toast.error('Video upload failed. Please try again.');
          return;
        }
        finalInviteData = {
          ...inviteData,
          mediaType: 'video',
          mediaUrl: videoUrl,
          videoUrl,
        };
      }
      if (isImageInvite) {
        let imageUrl = uploadedImageUrl;
        if (!imageUrl && imageFile) {
          setUploadingImage(true);
          const up = await einviteService.uploadImageInvite(weddingId, activeOrg?.id, imageFile);
          imageUrl = up?.image_url;
          setUploadedImageUrl(imageUrl || '');
          setUploadingImage(false);
        }
        if (!imageUrl) {
          toast.error('Image upload failed. Please try again.');
          return;
        }
        finalInviteData = {
          ...inviteData,
          mediaType: 'image',
          mediaUrl: imageUrl,
          imageUrl,
        };
      }

      const result = await einviteService.publishInvite(
        weddingId,
        activeOrg?.id,
        'media-invite',
        finalInviteData
      );
      
      setPublished(true);
      setPublishedInvite(result);
      
      // Refresh the list of all published invites
      try {
        const invites = await einviteService.getAllPublishedInvites(activeOrg.id);
        setAllPublishedInvites(invites || []);
      } catch (error) {
        console.error('Failed to refresh published invites:', error);
      }
      
      toast.success('E-invite published successfully! You can now share it with your guests.');
      setPreviewOpen(false);
      setShareDialogOpen(true); // Open share dialog to show the URL
    } catch (error) {
      console.error('Failed to publish invite:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to publish invite';
      toast.error(errorMessage);
    } finally {
      setUploadingVideo(false);
      setUploadingImage(false);
      setLoading(false);
    }
  };

  // Load published invite on mount
  useEffect(() => {
    const loadPublishedInvite = async () => {
      if (!activeOrg?.id || !weddingId) return;
      
      try {
        const published = await einviteService.getPublishedInvite(weddingId, activeOrg.id);
        if (published) {
          setPublished(true);
          setPublishedInvite(published);
        }
      } catch (error) {
        // Not published yet, that's fine
        console.log('No published invite found');
      }
    };

    loadPublishedInvite();
  }, [activeOrg?.id, weddingId]);

  // Load all published invites for the organization
  useEffect(() => {
    const loadAllPublishedInvites = async () => {
      if (!activeOrg?.id) return;
      
      try {
        setLoadingInvites(true);
        const invites = await einviteService.getAllPublishedInvites(activeOrg.id);
        setAllPublishedInvites(invites || []);
      } catch (error) {
        console.error('Failed to load published invites:', error);
        setAllPublishedInvites([]);
      } finally {
        setLoadingInvites(false);
      }
    };

    loadAllPublishedInvites();
  }, [activeOrg?.id]);

  const getInviteLabel = (invite) => {
    const mediaType =
      invite?.invite_data?.mediaType ||
      invite?.invite_data?.videoType ||
      invite?.invite_data?.imageType;
    if (mediaType === 'video') return 'Video Invite';
    if (mediaType === 'image') return 'Image Invite';
    if (invite?.template_id) return invite.template_id;
    return 'E-Invite';
  };

  const mediaPreviewUrl = isVideoInvite
    ? (videoPreviewUrl || uploadedVideoUrl)
    : (imagePreviewUrl || uploadedImageUrl);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold gradient-text">E-Invite</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Create beautiful digital wedding invitations
          </p>
        </div>
        {published && (
          <Button 
            variant="outline" 
            className="gap-2"
            onClick={() => setShareDialogOpen(true)}
          >
            <Send className="h-4 w-4" />
            Share Invite
          </Button>
        )}
      </div>

      {/* Published Status Banner */}
      {published && publishedInvite && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
              <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="font-semibold text-green-900 dark:text-green-100">E-Invite Published!</p>
              <p className="text-sm text-green-700 dark:text-green-300">
                Your invitation is live and ready to share with guests.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShareDialogOpen(true)}
            className="border-green-300 dark:border-green-700 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/40"
          >
            <Send className="h-4 w-4 mr-2" />
            Share
          </Button>
        </div>
      )}

      {/* All Published Invites Section */}
      {allPublishedInvites.length > 0 && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold">All Published Invites</h3>
                <p className="text-sm text-muted-foreground">
                  All your published E-invites across all weddings
                </p>
              </div>
            </div>
            <div className="space-y-3">
              {allPublishedInvites.map((invite) => (
                <div
                  key={invite.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex-1">
                    <p className="font-medium">{invite.wedding_name}</p>
                    <p className="text-sm text-muted-foreground">
                      Invite: {getInviteLabel(invite)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Published: {new Date(invite.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        window.open(invite.share_url, '_blank');
                      }}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(invite.share_url);
                        toast.success('Link copied to clipboard!');
                      }}
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Copy Link
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="design" className="w-full">
        <TabsList className="grid w-full grid-cols-1">
          <TabsTrigger value="design">
            <Palette className="h-4 w-4 mr-2" />
            Design
          </TabsTrigger>
        </TabsList>

        <TabsContent value="design" className="space-y-6">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Template Selection & Form */}
            <div className="lg:col-span-1 space-y-6">
              <Card>
                <CardContent className="p-6 space-y-4">
                  <Label className="text-base font-semibold mb-2 block">Invite Type</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={inviteType === 'video' ? 'default' : 'outline'}
                      onClick={() => {
                        setInviteType('video');
                        setImageFile(null);
                        setUploadedImageUrl('');
                        if (imagePreviewUrl) {
                          try { URL.revokeObjectURL(imagePreviewUrl); } catch (err) {}
                        }
                        setImagePreviewUrl('');
                      }}
                    >
                      Video Invite
                    </Button>
                    <Button
                      type="button"
                      variant={inviteType === 'image' ? 'default' : 'outline'}
                      onClick={() => {
                        setInviteType('image');
                        setVideoFile(null);
                        setUploadedVideoUrl('');
                        if (videoPreviewUrl) {
                          try { URL.revokeObjectURL(videoPreviewUrl); } catch (err) {}
                        }
                        setVideoPreviewUrl('');
                      }}
                    >
                      Image Invite
                    </Button>
                  </div>

                  {isVideoInvite && (
                    <div className="space-y-4">
                      <div>
                        <Label>Upload Video *</Label>
                        <Input
                          type="file"
                          accept="video/*"
                          onChange={(e) => {
                            const f = e.target.files?.[0] || null;
                            setVideoFile(f);
                            setUploadedVideoUrl('');
                            if (videoPreviewUrl) {
                              try { URL.revokeObjectURL(videoPreviewUrl); } catch (err) {}
                            }
                            if (f) {
                              const url = URL.createObjectURL(f);
                              setVideoPreviewUrl(url);
                            } else {
                              setVideoPreviewUrl('');
                            }
                          }}
                        />
                        <div className="text-xs text-muted-foreground mt-2">
                          Recommended: MP4 (H.264), under ~50MB for fast sharing.
                        </div>
                      </div>
                    </div>
                  )}

                  {isImageInvite && (
                    <div className="space-y-4">
                      <div>
                        <Label>Upload Image *</Label>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const f = e.target.files?.[0] || null;
                            setImageFile(f);
                            setUploadedImageUrl('');
                            if (imagePreviewUrl) {
                              try { URL.revokeObjectURL(imagePreviewUrl); } catch (err) {}
                            }
                            if (f) {
                              const url = URL.createObjectURL(f);
                              setImagePreviewUrl(url);
                            } else {
                              setImagePreviewUrl('');
                            }
                          }}
                        />
                        <div className="text-xs text-muted-foreground mt-2">
                          Recommended: JPG/PNG/WebP, under ~10MB for fast sharing.
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="flex gap-3">
                <Button
                  onClick={handlePreview}
                  className="flex-1 gradient-primary"
                  disabled={!canManage}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </Button>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          variant="outline"
                          onClick={handlePublish}
                          disabled={!canManage || loading || uploadingVideo || uploadingImage || isTrial}
                        >
                          <Send className="h-4 w-4 mr-2" />
                          {(loading || uploadingVideo || uploadingImage) ? 'Publishing...' : 'Publish'}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {isTrial && (
                      <TooltipContent className="bg-yellow-600 text-white border-yellow-600">
                        <p>Upgrade to publish and share E-invites</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            {/* Live Preview */}
            <div className="lg:col-span-2">
              <Card className="sticky top-6">
                <CardContent className="p-6">
                  <div className="bg-muted/30 rounded-lg p-4 min-h-[600px] flex items-center justify-center relative">
                    <div className="w-full max-w-md mx-auto">
                      {mediaPreviewUrl ? (
                        isVideoInvite ? (
                          <div className="rounded-xl overflow-hidden border bg-black">
                            <video
                              controls
                              playsInline
                              className="w-full h-auto"
                              src={mediaPreviewUrl}
                            />
                          </div>
                        ) : (
                          <div className="rounded-xl overflow-hidden border bg-card">
                            <img
                              src={mediaPreviewUrl}
                              alt="Invite preview"
                              className="w-full h-auto object-contain"
                            />
                          </div>
                        )
                      ) : (
                        <div className="rounded-xl border bg-card p-6 text-center text-muted-foreground">
                          Upload a {isVideoInvite ? 'video' : 'image'} to preview it here.
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

      </Tabs>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview E-Invite</DialogTitle>
          </DialogHeader>
          <div className="bg-muted/30 rounded-lg p-8 flex items-center justify-center my-4">
            <div className="w-full max-w-md mx-auto">
              {mediaPreviewUrl ? (
                isVideoInvite ? (
                  <div className="rounded-xl overflow-hidden border bg-black">
                    <video
                      controls
                      playsInline
                      className="w-full h-auto"
                      src={mediaPreviewUrl}
                    />
                  </div>
                ) : (
                  <div className="rounded-xl overflow-hidden border bg-card">
                    <img
                      src={mediaPreviewUrl}
                      alt="Invite preview"
                      className="w-full h-auto object-contain"
                    />
                  </div>
                )
              ) : (
                <div className="rounded-xl border bg-card p-6 text-center text-muted-foreground">
                  Upload a {isVideoInvite ? 'video' : 'image'} to preview it here.
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>
              Close
            </Button>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button onClick={handlePublish} className="gradient-primary" disabled={loading || uploadingVideo || uploadingImage || isTrial}>
                      <Send className="h-4 w-4 mr-2" />
                      {(loading || uploadingVideo || uploadingImage) ? 'Publishing...' : 'Publish Invite'}
                    </Button>
                  </span>
                </TooltipTrigger>
                {isTrial && (
                  <TooltipContent className="bg-yellow-600 text-white border-yellow-600">
                    <p>Upgrade to publish and share E-invites</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Invite Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Share Your E-Invite</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {publishedInvite?.share_url ? (
              <>
                <div>
                  <Label className="text-sm font-medium mb-2 block">Share Link</Label>
                  <div className="flex gap-2">
                    <Input
                      value={publishedInvite.share_url}
                      readOnly
                      className="font-mono text-sm"
                    />
                    <CopyLinkButton url={publishedInvite.share_url} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Share this link with your guests. They can view the invitation without logging in.
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      window.open(publishedInvite.share_url, '_blank');
                    }}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open in New Tab
                  </Button>
                </div>
                <div className="pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    💡 <strong>Tip:</strong> You can also share this invite from the <strong>Guests</strong> module by selecting guests and clicking "Share E-invite".
                  </p>
                </div>
              </>
            ) : (
              <p className="text-muted-foreground">Loading share link...</p>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setShareDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Copy Link Button Component
function CopyLinkButton({ url }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy link');
    }
  };

  return (
    <Button
      variant="outline"
      size="icon"
      onClick={handleCopy}
      className="shrink-0"
    >
      {copied ? (
        <Check className="h-4 w-4 text-green-600" />
      ) : (
        <Copy className="h-4 w-4" />
      )}
    </Button>
  );
}

